import { useMemo } from "react";
import { Column, Table } from "@tanstack/react-table";

export const dateRangeFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId);
  if (!cellValue) return false;
  if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;

  const cellDate = new Date(cellValue);
  const startDate = filterValue[0] ? new Date(filterValue[0]) : null;
  const endDate = filterValue[1] ? new Date(filterValue[1]) : null;

  if (startDate && cellDate < startDate) return false;
  if (endDate && cellDate > endDate) return false;

  return true;
};

export const arrayTextFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId) as string[];
  if (!cellValue) return false;

  if (!filterValue) return true;

  return cellValue.some((item) => String(item).toLowerCase().includes(filterValue.toLowerCase()));
};

export const selectFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId);
  return String(cellValue) === String(filterValue);
};

export const arraySelectFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId) as string[];
  if (!cellValue) return false;
  if (!filterValue) return true;
  return cellValue.some((item) => String(item) === String(filterValue));
};

export const arrayNumberRangeFilterFn = (row: any, columnId: string, filterValue: any) => {
  const cellValue = row.getValue(columnId) as string[];
  if (!cellValue) return false;
  if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;
  const min = filterValue[0] !== "" && filterValue[0] != null ? Number(filterValue[0]) : null;
  const max = filterValue[1] !== "" && filterValue[1] != null ? Number(filterValue[1]) : null;
  return cellValue.some((item) => {
    const num = Number(item);
    if (isNaN(num)) return false;
    if (min !== null && num < min) return false;
    if (max !== null && num > max) return false;
    return true;
  });
};

function Filter({ column, table }: { column: Column<any, any>; table: Table<any> }) {
  const firstValue = table.getPreFilteredRowModel().flatRows
    .map((row) => row.getValue(column.id))
    .find((val) => val != null);

  const columnFilterValue = column.getFilterValue();

  const { filterVariant } = (column.columnDef.meta as any) ?? {};

  const sortedUniqueValues = useMemo(() => {
    if (filterVariant === "select") {
      const uniqueValues = Array.from(
        new Set(table.getPreFilteredRowModel().flatRows.map((row) => row.getValue(column.id)))
      );
      uniqueValues.sort();
      return uniqueValues;
    }
    if (filterVariant === "arraySelect") {
      const uniqueValues = Array.from(
        new Set(
          table.getPreFilteredRowModel().flatRows.flatMap((row) => {
            const val = row.getValue(column.id) as string[];
            return Array.isArray(val) ? val.filter((v) => v !== "" && v != null) : [];
          })
        )
      );
      uniqueValues.sort();
      return uniqueValues;
    }
    return [];
  }, [column.id, filterVariant, table]);

  if (filterVariant === "select" || filterVariant === "arraySelect") {
    return (
      <select
        className="w-100 border shadow rounded"
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
        onClick={(e) => e.stopPropagation()}
        value={columnFilterValue?.toString() || ""}
      >
        <option value="">All</option>
        {sortedUniqueValues.map((value: any) => (
          <option value={value} key={value}>
            {value}
          </option>
        ))}
      </select>
    );
  }

  if (filterVariant === "arrayNumberRange") {
    return (
      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
        <div>
          <input
            type="number"
            value={(columnFilterValue as [number, number])?.[0] ?? ""}
            onChange={(e) => column.setFilterValue((old: [number, number]) => [e.target.value, old?.[1]])}
            placeholder="Min"
            className="border shadow rounded"
            style={{ width: "80px" }}
          />
        </div>
        <div>
          <input
            type="number"
            value={(columnFilterValue as [number, number])?.[1] ?? ""}
            onChange={(e) => column.setFilterValue((old: [number, number]) => [old?.[0], e.target.value])}
            placeholder="Max"
            className="border shadow rounded"
            style={{ width: "80px" }}
          />
        </div>
      </div>
    );
  }

  // Check if column is a date type (Date object or ISO date string)
  const isDateColumn =
    firstValue instanceof Date ||
    (typeof firstValue === "string" && /^\d{4}-\d{2}-\d{2}/.test(firstValue) && !isNaN(Date.parse(firstValue)));

  if (typeof firstValue === "boolean") {
    return (
      <select
        className="w-100 border shadow rounded"
        value={columnFilterValue === undefined ? "" : String(columnFilterValue)}
        onChange={(e) => {
          const value = e.target.value;
          column.setFilterValue(value === "" ? undefined : value === "true");
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">All</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    );
  }

  if (filterVariant === "arrayNumberRange") {
    return (
      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
        <div>
          <input
            type="number"
            value={(columnFilterValue as [number, number])?.[0] ?? ""}
            onChange={(e) => column.setFilterValue((old: [number, number]) => [e.target.value, old?.[1]])}
            placeholder="Min"
            className="border shadow rounded"
            style={{ width: "80px" }}
          />
        </div>
        <div>
          <input
            type="number"
            value={(columnFilterValue as [number, number])?.[1] ?? ""}
            onChange={(e) => column.setFilterValue((old: [number, number]) => [old?.[0], e.target.value])}
            placeholder="Max"
            className="border shadow rounded"
            style={{ width: "80px" }}
          />
        </div>
      </div>
    );
  }

  if (isDateColumn) {
    return (
      <div className="d-flex flex-column gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          type="date"
          value={(columnFilterValue as [string, string])?.[0] ?? ""}
          onChange={(e) => column.setFilterValue((old: [string, string]) => [e.target.value, old?.[1]])}
          placeholder="Start Date"
          className="w-100 border shadow rounded"
          style={{ fontSize: "0.875rem" }}
        />
        <input
          type="date"
          value={(columnFilterValue as [string, string])?.[1] ?? ""}
          onChange={(e) => column.setFilterValue((old: [string, string]) => [old?.[0], e.target.value])}
          placeholder="End Date"
          className="w-100 border shadow rounded"
          style={{ fontSize: "0.875rem" }}
        />
      </div>
    );
  }

  return typeof firstValue === "number" ? (
    <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
      <div>
        <input
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(e) => column.setFilterValue((old: [number, number]) => [e.target.value, old?.[1]])}
          placeholder={`Min`}
          className="border shadow rounded"
          style={{ width: "80px" }}
        />
      </div>
      <div>
        <input
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(e) => column.setFilterValue((old: [number, number]) => [old?.[0], e.target.value])}
          placeholder={`Max`}
          className="border shadow rounded"
          style={{ width: "80px" }}
        />
      </div>
    </div>
  ) : (
    <input
      className="w-36 border shadow rounded"
      onChange={(e) => column.setFilterValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? "") as string}
    />
  );
}

export default Filter;
